import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { SnackBarService } from './snackbar.service';
import { SupabaseService } from './supabase.service';

/**
 * One in-app notification row (public.notifications, own rows only).
 * `read` is DERIVED: the table has no read column — read state lives in
 * public.dismissed_notifications (own-rows SELECT policy, 20260704160000),
 * embedded on fetch. Live realtime rows arrive with no dismissals = unread.
 */
export interface AppNotification {
  id: string;
  created_at: string;
  title: string;
  body: string | null;
  read: boolean;
  club_id: string | null;
  from_user: string | null;
}

/**
 * Notification rows arrive from the DB triggers with ENGLISH title/body
 * (migrations 20260622150000 + 20260704150000). Pure mapper: KNOWN titles map
 * to i18n keys and KNOWN body patterns to translated equivalents — the dynamic
 * parts (athlete/club name, parenthesized "when", trailing quoted message) are
 * extracted via regex and re-interpolated. Anything unrecognized falls back to
 * the raw text untouched.
 */
export function translateNotification(
  n: Pick<AppNotification, 'title' | 'body'>,
  translate: TranslateService
): { title: string; body: string | null } {
  const titleKeys: Record<string, string> = {
    'New visit request': 'notifications.newVisitRequestTitle',
    'Request accepted': 'notifications.acceptedTitle',
    'Request declined': 'notifications.declinedTitle',
  };
  const titleKey = titleKeys[n.title];
  const title: string = titleKey ? translate.instant(titleKey) : n.title;

  let body = n.body;
  if (body) {
    let m = body.match(/^(.+?) requested a visit \((.+?)\)\.(?: "([\s\S]*)")?$/);
    if (m) {
      const name =
        m[1] === 'An athlete'
          ? translate.instant('notifications.genericAthlete')
          : m[1];
      const when =
        m[2] === 'no preferred time'
          ? translate.instant('notifications.noPreferredTime')
          : m[2];
      body = translate.instant('notifications.visitRequestBody', {
        name,
        when,
      });
      if (m[3] !== undefined) body += ` "${m[3]}"`;
    } else if (
      (m = body.match(/^(.+) accepted your visit request\. See you there!$/))
    ) {
      const club =
        m[1] === 'The club'
          ? translate.instant('notifications.genericClub')
          : m[1];
      body = translate.instant('notifications.acceptedBody', { club });
    } else if ((m = body.match(/^(.+) declined your visit request\.$/))) {
      const club =
        m[1] === 'The club'
          ? translate.instant('notifications.genericClub')
          : m[1];
      body = translate.instant('notifications.declinedBody', { club });
    }
  }
  return { title, body };
}

/**
 * The frontend notifications hub: one service to RECEIVE (live) and SEND
 * in-app notifications.
 *
 * Receive: reads the caller's own rows directly (RLS policy "read own
 * notifications" scopes the query server-side — works for athletes AND admins,
 * replacing the bell's per-admin-club RPC fan-out), and subscribes to Supabase
 * Realtime for INSERTs addressed to the user, so the badge updates and a toast
 * can fire without any polling or reload.
 *
 * Send: `send()` calls the gated SECURITY DEFINER RPC public.send_notification
 * (migration 20260704150000) — an authenticated user can notify only someone
 * they share a club_requests relationship with.
 *
 * Depends on that migration; every path degrades gracefully (empty list, no
 * live channel, send error surfaced to the caller) when it isn't applied yet.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly supabase = inject(SupabaseService);
  private readonly snackbar = inject(SnackBarService);

  private readonly _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();

  private readonly _latest = signal<AppNotification[]>([]);
  readonly latest = this._latest.asReadonly();

  /** Fires for every live-arriving notification — drives the toast host. */
  readonly incoming$ = new Subject<AppNotification>();

  private channel: ReturnType<SupabaseService['client']['channel']> | null =
    null;
  private userId: string | null = null;

  /** Bind to the signed-in user: initial load + live subscription. Idempotent. */
  async init(): Promise<void> {
    const user = await this.supabase.getCurrentUser();
    if (!user) {
      this.teardown();
      return;
    }
    if (this.userId === user.id && this.channel) return; // already bound
    this.teardown();
    this.userId = user.id;
    await this.refresh();
    this.listen(user.id);
  }

  /** Drop the live channel + state (call on sign-out). */
  teardown(): void {
    if (this.channel) {
      void this.supabase.client.removeChannel(this.channel);
      this.channel = null;
    }
    this.userId = null;
    this._unreadCount.set(0);
    this._latest.set([]);
  }

  /** Reload the newest rows + unread count (RLS scopes to own rows). */
  async refresh(limit = 15): Promise<void> {
    if (!this.userId) return;
    const { data, error } = await this.supabase.client
      .from('notifications')
      .select(
        'id, created_at, title, body, club_id, from_user, dismissed_notifications(user_id)'
      )
      .eq('to_user', this.userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      // Policy not deployed yet → keep the UI quiet rather than erroring.
      console.warn('notifications refresh failed:', error.message);
      return;
    }
    const rows = ((data ?? []) as any[]).map((r) => ({
      id: r.id,
      created_at: r.created_at,
      title: r.title,
      body: r.body,
      club_id: r.club_id,
      from_user: r.from_user,
      read: Array.isArray(r.dismissed_notifications)
        ? r.dismissed_notifications.length > 0
        : false,
    })) as AppNotification[];
    this._latest.set(rows);
    // Unread = undismissed among the newest rows (badge caps at 9+ anyway).
    this._unreadCount.set(rows.filter((r) => !r.read).length);
  }

  /** Live INSERTs addressed to this user → state + toast stream. */
  private listen(userId: string): void {
    void this.subscribeWithRetry(userId, 0);
  }

  /**
   * Subscribe with the session token explicitly set on the realtime socket and
   * retry on failure. Right after sign-in the socket can still be authorized as
   * anon, so the RLS check rejects the subscription (CHANNEL_ERROR) — a one-shot
   * subscribe then stays dead for the whole session.
   */
  private async subscribeWithRetry(
    userId: string,
    attempt: number
  ): Promise<void> {
    if (this.userId !== userId) return; // signed out / switched meanwhile
    try {
      const { data } = await this.supabase.client.auth.getSession();
      const token = data.session?.access_token;
      if (token) this.supabase.client.realtime.setAuth(token);
      this.channel = this.supabase.client
        .channel(`notifications:${userId}:${attempt}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `to_user=eq.${userId}`,
          },
          (payload) => {
            const raw = payload.new as any;
            const n: AppNotification = { ...raw, read: false };
            this._latest.set([n, ...this._latest()].slice(0, 15));
            this._unreadCount.set(this._unreadCount() + 1);
            this.incoming$.next(n);
          }
        )
        .subscribe((status) => {
          if (
            (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') &&
            this.userId === userId
          ) {
            if (this.channel) {
              void this.supabase.client.removeChannel(this.channel);
              this.channel = null;
            }
            if (attempt < 5) {
              setTimeout(
                () => void this.subscribeWithRetry(userId, attempt + 1),
                1500 * (attempt + 1)
              );
            } else {
              console.warn('notifications realtime gave up after retries');
            }
          }
        });
    } catch (e) {
      console.warn('notifications realtime unavailable:', e);
    }
  }

  /** Mark one row read (uses the existing per-row RPC path). */
  async markRead(n: AppNotification): Promise<void> {
    const ok = await this.doMarkRead(n);
    if (!ok) this.snackbar.errorKey('notifications.markReadFailed');
  }

  async markAllRead(): Promise<void> {
    const unread = this._latest().filter((n) => !n.read);
    const results = await Promise.all(unread.map((n) => this.doMarkRead(n)));
    // One snackbar for the whole batch, not one per failed row.
    if (results.includes(false)) {
      this.snackbar.errorKey('notifications.markReadFailed');
    }
  }

  /**
   * RPC + local state for one row; returns success instead of toasting so
   * markAllRead can surface a single error. State only updates when the RPC
   * succeeded (previously it updated optimistically even on failure).
   */
  private async doMarkRead(n: AppNotification): Promise<boolean> {
    if (!this.userId || n.read) return true;
    const ok = await this.supabase.markNotificationRead(this.userId, n.id);
    if (!ok) return false;
    this._latest.set(
      this._latest().map((x) => (x.id === n.id ? { ...x, read: true } : x))
    );
    this._unreadCount.set(Math.max(0, this._unreadCount() - 1));
    return true;
  }

  /**
   * Send an in-app notification from the frontend. Gated server-side: the
   * recipient must share a club_requests relationship with the sender.
   * Returns the new notification id, or null on failure (logged + surfaced
   * to the user via snackbar).
   */
  async send(
    toUser: string,
    title: string,
    body: string,
    clubId?: string | null
  ): Promise<string | null> {
    const { data, error } = await this.supabase.client.rpc(
      'send_notification' as never,
      {
        p_to_user: toUser,
        p_title: title,
        p_body: body,
        p_club_id: clubId ?? null,
      } as never
    );
    if (error) {
      console.error('send_notification failed:', error.message);
      this.snackbar.errorKey('notifications.sendFailed');
      return null;
    }
    return (data as unknown as string) ?? null;
  }
}
